"use client"

import { useRef, useState, useEffect } from "react"
import { Input } from "../ui/input"
import { Button } from "../ui/button"
import { toast } from "sonner"
import { post } from "../../lib/api"
import { readFileWithEncodingDetection } from "../../lib/encoding-utils"

// InfoOverlay component
const InfoOverlay = ({ open, onClose }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 ">
            <div className="bg-[#ffffff] rounded-lg shadow-lg max-w-lg w-full p-6 relative dark:bg-[#2c313c] dark:text-white">
                <button
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl font-bold"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ×
                </button>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                        <path d="M12 16v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    How does the Wordle uploader work?
                </h2>
                <div className="space-y-3 text-gray-800 text-sm dark:text-white">
                    <p>
                        Upload a <b>.txt file</b> with 5-letter words, one per line, or enter a word manually. The uploader will only accept valid 5-letter words (A-Z).
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Click the blue button to upload a .txt file, or type a word manually and click Load Word.</li>
                        <li>Each word must be exactly 5 letters, no spaces or numbers.</li>
                        <li>If you upload multiple words, they will be scheduled for consecutive days starting from your selected date.</li>
                        <li>After loading, you can preview and schedule the challenge.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

const WordlePlanning = ({ game_id, onUpdate }) => {
    const [words, setWords] = useState([])
    const [currentWordIndex, setCurrentWordIndex] = useState(0)
    const [manualWord, setManualWord] = useState("")
    const [isManualMode, setIsManualMode] = useState(false)
    const [scheduledDate, setScheduledDate] = useState("")
    const [lastUploadedDate, setLastUploadedDate] = useState(null)
    const [isUploading, setIsUploading] = useState(false)
    const fileInputRef = useRef(null)
    const [showInfo, setShowInfo] = useState(false)

    // Initialize with today's date
    useEffect(() => {
        const today = new Date()
        const todayString = today.toISOString().split("T")[0]
        setScheduledDate(todayString)
    }, [])

    // Update default date after upload
    useEffect(() => {
        if (lastUploadedDate) {
            const nextDay = new Date(lastUploadedDate)
            nextDay.setDate(nextDay.getDate() + 1)
            const nextDayString = nextDay.toISOString().split("T")[0]
            setScheduledDate(nextDayString)
        }
    }, [lastUploadedDate])

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file || file.type !== "text/plain") {
            toast.error("Please upload a valid .txt file.")
            return
        }

        try {
            const text = await readFileWithEncodingDetection(file)
            const lines = text
                .split("\n")
                .map((line) => line.trim().toUpperCase())
                .filter((line) => line.length === 5 && /^[A-Z]+$/.test(line))

            if (lines.length === 0) {
                toast.error("No valid 5-letter words found in the file.")
                return
            }

            setWords(lines)
            setCurrentWordIndex(0)
            setIsManualMode(false)
            toast.success(`Loaded ${lines.length} valid 5-letter words!`)
        } catch (error) {
            toast.error("Failed to read file. Please try again.")
            console.error("File reading error:", error)
        }
    }

    const handleManualWordSubmit = () => {
        const word = manualWord.trim().toUpperCase()

        if (word.length !== 5) {
            toast.error("Please enter exactly 5 letters!")
            return
        }

        if (!/^[A-Z]+$/.test(word)) {
            toast.error("Please enter only letters!")
            return
        }

        setWords([word])
        setCurrentWordIndex(0)
        setIsManualMode(true)
        toast.success(`Word "${word}" loaded successfully!`)
    }

    const getCurrentWord = () => words[currentWordIndex] || ""

    const nextWord = () => {
        if (currentWordIndex < words.length - 1) {
            setCurrentWordIndex(currentWordIndex + 1)
        }
    }

    const prevWord = () => {
        if (currentWordIndex > 0) {
            setCurrentWordIndex(currentWordIndex - 1)
        }
    }

    const formatDateForDisplay = (dateString) => {
        const date = new Date(dateString)
        return date.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        })
    }

    const handleUploadPuzzle = async () => {
        const word = getCurrentWord()

        if (!word) {
            toast.error("No valid word to upload.")
            return
        }

        if (!scheduledDate) {
            toast.error("Please select a date for the challenge.")
            return
        }

        setIsUploading(true)

        try {
            // First API call - Add the challenge
            const response = await post(
                {
                    method: "addChallengeNoDup",
                    game_id: game_id,
                    challenge_id: word, // Solution is the challenge name
                    data: word, // For Wordle, the data is the same as the solution
                    variant_id: 1,
                    variant_name: "challenge",
                },
                "/challenges",
            )

            if (response.error) {
                if (response.error === "Failed to fetch") {
                    toast.error("A challenge with that name already exists.")
                } else {
                    toast.error(response.error)
                }
                return
            }

            // Second API call - Schedule the daily challenge
            const dailyResponse = await post(
                {
                    method: "addDailyChallenge",
                    game_id: game_id,
                    date: scheduledDate,
                    daily_challenge_info: word, // Use the word as the daily challenge info
                },
                "/challenges",
            )

            if (dailyResponse.error) {
                toast.error(`Challenge uploaded but scheduling failed: ${dailyResponse.error}`)
                return
            }

            toast.success(`Wordle challenge "${word}" uploaded and scheduled for ${formatDateForDisplay(scheduledDate)}!`)
            setLastUploadedDate(scheduledDate) // Update the last uploaded date
            onUpdate?.()

            // If there are more words, move to the next one
            if (currentWordIndex < words.length - 1) {
                setCurrentWordIndex(currentWordIndex + 1)
                // Update the scheduled date to the next day
                const nextDay = new Date(scheduledDate)
                nextDay.setDate(nextDay.getDate() + 1)
                const nextDayString = nextDay.toISOString().split("T")[0]
                setScheduledDate(nextDayString)
            }
        } catch (err) {
            toast.error("Upload failed!")
            console.error(err)
        } finally {
            setIsUploading(false)
        }
    }

    const handleUploadAllWords = async () => {
        if (words.length === 0) {
            toast.error("No words to upload.")
            return
        }

        if (!scheduledDate) {
            toast.error("Please select a start date for the challenges.")
            return
        }

        setIsUploading(true)

        try {
            let currentDate = new Date(scheduledDate)
            let successCount = 0

            for (let i = 0; i < words.length; i++) {
                const word = words[i]
                const dateString = currentDate.toISOString().split("T")[0]

                // First API call - Add the challenge
                const response = await post(
                    {
                        method: "addChallengeNoDup",
                        game_id: game_id,
                        challenge_id: word,
                        data: word,
                        variant_id: 1,
                        variant_name: "challenge",
                    },
                    "/challenges",
                )

                if (response.error) {
                    if (response.error !== "Failed to fetch") {
                        toast.error(`Failed to upload "${word}": ${response.error}`)
                        continue
                    }
                }

                // Second API call - Schedule the daily challenge
                const dailyResponse = await post(
                    {
                        method: "addDailyChallenge",
                        game_id: game_id,
                        date: dateString,
                        daily_challenge_info: word,
                    },
                    "/challenges",
                )

                if (dailyResponse.error) {
                    toast.error(`Challenge "${word}" uploaded but scheduling failed: ${dailyResponse.error}`)
                } else {
                    successCount++
                }

                // Move to next day
                currentDate.setDate(currentDate.getDate() + 1)
            }

            if (successCount > 0) {
                toast.success(`Successfully uploaded ${successCount} out of ${words.length} Wordle challenges!`)
                setLastUploadedDate(currentDate.toISOString().split("T")[0])
                onUpdate?.()
            }
        } catch (err) {
            toast.error("Upload failed!")
            console.error(err)
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <>
            <div className="space-y-4">
                {/* Manual Word Entry */}
                <div className="flex items-center gap-3 mb-2">
                    <Input type="file" accept=".txt" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
                    <Button
                        className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white border border-blue-700 dark:border-blue-700 px-6 py-3 font-semibold shadow-md"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Upload 5-Letter Words File
                    </Button>
                    <span className="text-gray-500 dark:text-gray-400">or</span>
                    <Input
                        type="text"
                        placeholder="Enter 5-letter word manually"
                        value={manualWord}
                        onChange={(e) => setManualWord(e.target.value.toUpperCase())}
                        maxLength={5}
                        className="max-w-xs text-gray-900 dark:text-white dark:bg-gray-800 dark:placeholder-gray-500 dark:border-gray-600 placeholder-gray-500 border border-gray-300 dark:border-gray-600"
                        onKeyPress={(e) => e.key === "Enter" && handleManualWordSubmit()}
                    />
                    <Button
                        className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white border border-green-700 dark:border-green-700 px-6 py-3 font-semibold shadow-md"
                        onClick={handleManualWordSubmit}
                    >
                        Load Word
                    </Button>
                </div>
            </div>

            {words.length > 0 && (
                <>
                    {/* Word Navigation - Only show if not in manual mode or if manual mode has multiple words */}
                    {(!isManualMode || words.length > 1) && (
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-2">
                            <Button
                                variant="outline"
                                className="border-gray-400 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white px-4 py-2 font-medium shadow-sm"
                                onClick={prevWord}
                                disabled={currentWordIndex === 0}
                            >
                                Previous Word
                            </Button>
                            <div className="text-center">
                                <div className="font-bold text-lg text-gray-900 dark:text-white">
                                    Word {currentWordIndex + 1} of {words.length}: {getCurrentWord()}
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                    Challenge Word: {getCurrentWord()}
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                className="border-gray-400 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white px-4 py-2 font-medium shadow-sm"
                                onClick={nextWord}
                                disabled={currentWordIndex === words.length - 1}
                            >
                                Next Word
                            </Button>
                        </div>
                    )}

                    {/* Manual mode single word display */}
                    {isManualMode && words.length === 1 && (
                        <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-2">
                            <div className="font-bold text-lg text-gray-900 dark:text-white">Current Word: {getCurrentWord()}</div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                                Challenge Word: {getCurrentWord()}
                            </div>
                        </div>
                    )}

                    {/* Word Display */}
                    <div className="flex justify-center py-6 mb-2">
                        <div className="p-8 bg-[#ffffff] dark:bg-[#2c313c] border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
                            <div className="text-center">
                                <div className="text-4xl font-bold text-gray-900 dark:text-white mb-4 flex justify-center">
                                    {getCurrentWord().split('').map((letter, index) => (
                                        <span
                                            key={index}
                                            className="inline-block w-12 h-12 mx-1 border-2 border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-[#ffffff] dark:bg-gray-800 text-gray-900 dark:text-white"
                                        >
                                            {letter}
                                        </span>
                                    ))}
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                    5-letter Wordle Challenge
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Word Info */}
                    <div className="p-4 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-900 rounded-lg mb-2">
                        <div className="text-base text-gray-900 dark:text-white space-y-1">
                            <div>
                                <strong>Challenge Word:</strong> {getCurrentWord()}
                            </div>
                            <div>
                                <strong>Word Length:</strong> {getCurrentWord().length} letters
                            </div>
                        </div>
                    </div>

                    {/* Date Scheduling */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-900 rounded-lg mb-2">
                        <div className="space-y-3">
                            <label htmlFor="scheduledDate" className="block text-sm font-medium text-gray-900 dark:text-white">
                                Schedule Challenge Date:
                            </label>
                            <div className="flex items-center gap-4">
                                <Input
                                    type="date"
                                    id="scheduledDate"
                                    value={scheduledDate}
                                    onChange={(e) => setScheduledDate(e.target.value)}
                                    className="max-w-xs text-gray-900 dark:text-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600"
                                />
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                    {scheduledDate && `Will be scheduled for: ${formatDateForDisplay(scheduledDate)}`}
                                </div>
                            </div>
                            {lastUploadedDate && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Last upload was scheduled for {formatDateForDisplay(lastUploadedDate)}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Upload Buttons */}
                    <div className="space-y-2">
                        <Button
                            className="w-full bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white border border-green-700 dark:border-green-700 py-3 font-semibold shadow-md"
                            onClick={handleUploadPuzzle}
                            disabled={isUploading}
                        >
                            {isUploading ? "Uploading..." : `Schedule This Word for ${scheduledDate ? formatDateForDisplay(scheduledDate) : "Selected Date"}`}
                        </Button>
                        
                        {words.length > 1 && (
                            <Button
                                className="w-full bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-800 text-white border border-purple-700 dark:border-purple-700 py-3 font-semibold shadow-md"
                                onClick={handleUploadAllWords}
                                disabled={isUploading}
                            >
                                {isUploading ? "Uploading All..." : `Upload All ${words.length} Words (Consecutive Days)`}
                            </Button>
                        )}
                    </div>
                </>
            )}

            {words.length === 0 && (
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-md mb-4 mt-4">
                    <span>
                        📁 Upload a .txt file with 5-letter words or enter a word manually to get started
                    </span>
                    <button
                        className="ml-2 p-1 rounded-full flex items-center justify-center"
                        onClick={() => setShowInfo(true)}
                        aria-label="How does the uploader work?"
                        type="button"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                            <path d="M12 16v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>
            )}
            <InfoOverlay open={showInfo} onClose={() => setShowInfo(false)} />
        </>
    )
}

export default WordlePlanning 